import React, { useEffect, useRef, useState } from "react";
import { TImages } from "models/inventory/interfaces/vehicle.interface";
import { ReactComponent as Arrow } from "design_system/src/static/icons/up-lg.svg";
import { Button, ScalableImage, Typography } from "design_system/src";
import { VehicleDetailImage } from "models/inventory/interfaces/vehicle-detail.interfaces";
import { translateImageType } from "views/VehicleDetail/helpers/translations";
import styles from "./Photos.module.scss";


interface PhotosMobileProps {
  photos: TImages[] | VehicleDetailImage[];
  showThumbsRow?: boolean;
  showLenderImg?: boolean;
}

const isFeaturedPhoto = (photo: TImages | VehicleDetailImage): photo is VehicleDetailImage => {
  return "imageType" in photo;
};

const PhotosMobile: React.FC<PhotosMobileProps> = ({
  photos,
  showThumbsRow = true,
  showLenderImg = false
}) => {
  if (photos.length === 0) return null;

  const showArrows = photos.length > 1;
  const [current, setCurrent] = useState(0);

  const goTo = (idx: number) => setCurrent(idx);
  const prev = () => setCurrent((_prev) => (_prev === 0 ? photos.length - 1 : _prev - 1));
  const next = () => setCurrent((_prev) => (_prev === photos.length - 1 ? 0 : _prev + 1));

  const getMainUrl = (img: TImages) => img.url || img.formats?.small?.url || img.formats?.thumbnail?.url || "";
  const getThumbUrl = (img: TImages) => img.formats?.thumbnail?.url || img.url || "";

  const isFeaturedPhotosArray = photos.every(isFeaturedPhoto);

  const thumbsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!thumbsContainerRef.current) return;
    const thumbs = thumbsContainerRef.current.querySelectorAll("[data-thumb]");
    const activeThumb = thumbs[current] as HTMLElement | undefined;

    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [current]);

  return (
    <div className={`${styles.mobileSlider} m_b_xxl m_b_none_mobile`}>
      <div className={styles.mainImageWrapper}>
        {showArrows && <button onClick={prev} type="button" className={`${styles.mobileBtn} ${styles.mobileBtnLeft}`}>
          <Arrow className="text_primary_300" style={{ "transform": "rotate(-90deg)" }} />
        </button>}

        <ScalableImage
          className={`${styles.mainImage}${isFeaturedPhotosArray ? ` ${styles["mainImage--isFeatured"]}` : ""}`}
          src={getMainUrl(photos[current])}
          alt={photos[current].name}
        />

        {showArrows && <button onClick={next} type="button" className={`${styles.mobileBtn} ${styles.mobileBtnRight}`}>
          <Arrow className="text_primary_300" style={{ "transform": "rotate(90deg)" }} />
        </button>}

        {isFeaturedPhotosArray && <div className={styles.mainImageName}>
          <Typography weight="600" scale="small">
            {translateImageType((photos[current] as VehicleDetailImage).imageType)}
          </Typography>
        </div>}

        {showLenderImg && (
          <div
            className="pos_absolute m_t_md m_l_lg"
            style={{ top: 0, left: 0, }}
          >
            <ScalableImage
              className="h_20_px"
              src="https://ik.imagekit.io/ozon/Homepage/Logos/logo_alianza_walmart_cashi_bodega_aurrera_xsmall"
              alt="Logo de cashi - walmart"
              desktopWidth={10}
              mobileWidth={10}
            />
          </div>
        )}
      </div>

      {showThumbsRow && showArrows && <div className={styles.thumbsRow} ref={thumbsContainerRef}>
        {photos.map((img, idx) => (
          <ScalableImage
            key={img._id}
            data-thumb
            className={`${styles.thumb}${idx === current ? ` ${styles.thumbActive}` : "" }`}
            src={getThumbUrl(img)}
            alt={img.name}
            onClick={() => goTo(idx)}
          />
        ))}
      </div>}
    </div>
  );
};

export default PhotosMobile;
